/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type {Routes} from "@angular/router";
import {PermissionId} from "../../models/users/Permission";
import {UserAccountType} from "../../models/users/User";
import {hasPermissions, hasUserTypes, isLoggedIn} from "../../services/guards/permission-guard";

export const customerRoutes = [
  // Customer Database
  {
    path: "customer-database",
    canActivate: [isLoggedIn, hasPermissions(PermissionId.AccessCustomerDatabase)],
    loadComponent: () => import("./customer-database/customer-database").then(m => m.CustomerDatabase),
  },

  // Equipment
  {
    path: "equipment",
    canActivateChild: [isLoggedIn],
    children: [
      {
        path: "",
        canActivate: [hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce)],
        loadComponent: () => import("./equipment/list/equipment-list").then(m => m.EquipmentList),
      },
      {
        path: "inspection-types",
        children: [
          {
            path: "", // Previously inspection type extract
            canActivate: [hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce)],
            loadComponent: () => import("./equipment/inspection-types/equipment-inspection-types-overview").then(m => m.EquipmentInspectionTypesOverview),
          },
          {
            path: "update",
            canActivate: [hasPermissions(PermissionId.BulkUpdateEquipmentInspectionTypes)],
            loadComponent: () => import("./equipment/inspection-types/bulk-update/equipment-inspection-types-bulk-update").then(m => m.EquipmentInspectionTypesBulkUpdate),
          },
        ],
      },
      {
        path: "create",
        canActivate: [hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce)],
        loadComponent: () => import("./equipment/form/equipment-form").then(m => m.EquipmentForm),
      },
      {
        path: ":equipmentId",
        canActivate: [
          hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce, UserAccountType.Customer, UserAccountType.ThirdParty),
        ],
        loadComponent: () => import("./equipment/form/equipment-form").then(m => m.EquipmentForm),
      },
    ],
  },

  // Industry Sections
  {
    path: "industry-sections",
    canActivateChild: [isLoggedIn, hasUserTypes(UserAccountType.Admin)],
    children: [
      {
        path: "",
        loadComponent: () => import("./industry-sections/list/industry-sections-list").then(m => m.IndustrySectionsList),
      },
      {
        path: "create",
        loadComponent: () => import("./industry-sections/form/industry-section-form").then(m => m.IndustrySectionForm),
      },
      {
        path: ":industrySectionId",
        loadComponent: () => import("./industry-sections/form/industry-section-form").then(m => m.IndustrySectionForm),
      },
    ],
  },

  // Locations
  {
    path: "locations",
    canActivateChild: [isLoggedIn, hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce)],
    children: [
      {
        path: "",
        loadComponent: () => import("./locations/list/customer-locations-list").then(m => m.CustomerLocationsList),
      },
      {
        path: "create",
        loadComponent: () => import("./locations/form/customer-location-form").then(m => m.CustomerLocationForm),
      },
      {
        path: ":customerLocationId",
        children: [
          {
            path: "",
            loadComponent: () => import("./locations/form/customer-location-form").then(m => m.CustomerLocationForm),
          },
          // Departments
          {
            path: "departments",
            children: [
              {
                path: ":customerDepartmentId",
                loadComponent: () => import("./departments/form/customer-department-form").then(m => m.CustomerDepartmentForm),
              },
            ],
          },
        ],
      },
    ],
  },

  // Organizations
  {
    path: "organizations",
    canActivateChild: [isLoggedIn, hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce)],
    children: [
      {
        path: "",
        loadComponent: () => import("./organizations/list/customer-organizations-list").then(m => m.CustomerOrganizationsList),
      },
      {
        path: "create",
        canActivate: [hasPermissions(PermissionId.CreateCustomerOrganization)],
        loadComponent: () => import("./organizations/form/customer-organization-form").then(m => m.CustomerOrganizationForm),
      },
      {
        path: "merge",
        canActivate: [hasUserTypes(UserAccountType.Admin)],
        loadComponent: () => import("./organizations/merge/customer-organizations-merge").then(m => m.CustomerOrganizationsMerge),
      },
      {
        path: ":customerOrganizationId",
        loadComponent: () => import("./organizations/form/customer-organization-form").then(m => m.CustomerOrganizationForm),
      },
    ],
  },

  // Sales Log
  {
    path: "sales-log",
    canActivate: [
      isLoggedIn,
      hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce),
      hasPermissions(PermissionId.SeeSalesActivityLog),
    ],
    loadComponent: () => import("./sales-log/list/sales-log-list").then(m => m.SalesLogList),
  },
] as const satisfies Routes;
